import React, { useEffect, useMemo, useState } from "react";
import Loader from "@appComponents/Loader/Loader";
import { TReducers } from "@features/reducers";
import { Col, Form, Row, Typography, Divider, notification } from "antd";
import { useFormik } from "formik";
import { connect } from "react-redux";
import * as Yup from "yup";
import FundsService, { StakingResponse } from "@services/Funds.service";
import { StakingEthResponse } from "@funds/pages/MyFunds/tabs/FundsTab/parts/CurrencyDetails";
import { setFundsData } from "@funds/features/action.funds";
import CustomSteps from "@funds/components/CustomSteps";
import Section from "../shared/Section/Section";
import Faq from "../shared/Faq";
import { TimelockStep } from "./parts/Steps/TimelockStep/TimelockStep";
import { SummaryStep } from "./parts/Steps/SummaryStep/SummaryStep";
import { TransactionsStep } from "./parts/Steps/TransactionsStep/TransactionsStep";
import timelockStakingFaq from "./timelockStakingFaq";

import * as style from "./TimelockStakeFormStyle.module.scss";
import { appsignal } from "@bundles/appsignal";

type Props = {
  history: any;
  setFundsData: (investorId: number) => any;
  companyInvestorId: number | undefined;
  investorId: number;
  investorSlug: string;
  country?: string;
  companySlug: string;
};

const TimelockStakeForm = ({
  history,
  setFundsData,
  companyInvestorId,
  investorId,
  investorSlug,
  country,
  companySlug,
}: Props) => {
  const genericInvestorId = companyInvestorId || investorId;

  const isUk = useMemo(() => country === "GB", [country]);

  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [stakingTransactions, setStakingTransactions] = useState<
    StakingResponse[]
  >([]);
  const [stakingData, setStakingData] = useState<StakingEthResponse>();

  const appLink = companyInvestorId
    ? `/companies/${companySlug}/funds`
    : `/profile/${investorSlug}/funds`;

  useEffect(() => {
    Promise.all([
      setFundsData(genericInvestorId),
      FundsService.getStaking(),
      FundsService.getStakingEth(investorId),
    ])
      .then(([_, stakingTransactions, respStaking]) => {
        setIsLoading(false);
        setStakingData(respStaking.data);
        const availableKey = "available_staked_eth";
        const availableAmount =
          respStaking.data && respStaking.data[availableKey];
        var rollingSum = 0;
        setStakingTransactions(
          stakingTransactions.data
            .sort((a, b) => {
              const dateA = new Date(a.created_at).getTime();
              const dateB = new Date(b.created_at).getTime();
              return dateB - dateA;
            })
            .filter((staking) => {
              const statusCheck =
                staking.status === "completed" &&
                staking.timelock_duration === null;
              var underAvailable = false;
              if (statusCheck) {
                const tempRollingSum = rollingSum + Number(staking.amount);
                underAvailable = isNaN(availableAmount)
                  ? true
                  : tempRollingSum <= availableAmount;
              }
              if (underAvailable) {
                rollingSum += Number(staking.amount);
              }
              return statusCheck && underAvailable;
            })
        );
      })
      .catch(() => {
        setIsLoading(true);
      });
  }, []);

  const formik = useFormik({
    initialValues: {
      transactionIds: [],
      agreement1: false,
      timelockDuration: null,
    },
    validationSchema: Yup.object().shape({
      agreement1: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
    }),

    onSubmit: async (values) => {
      const { transactionIds, timelockDuration } = values;

      try {
        const resp = await FundsService.addTimelocks(
          transactionIds,
          timelockDuration
        );
        history.push(appLink + "/stakingEth");
        if (resp.data.failed_count) {
          notification.error({
            message: `Failed to timelock ${resp.data.failed_count} ${
              resp.data.failed_count > 1 ? "transactions" : "transaction"
            }.`,
          });
        } else {
          notification.success({
            message:
              "Confirmation: You've successfully timelocked your staked ETH.",
          });
        }
      } catch (error) {
        appsignal.sendError(error);

        if (error.response.status === 422) {
          notification.error({ message: error.response.data.error });
        } else {
          notification.error({ message: "Timelock Staking failed" });
        }
        throw new Error("timelock staking failed");
      }
    },
    validateOnMount: false,
  });

  const sumOfSelectedTransactions = useMemo(() => {
    return stakingTransactions
      .filter((x) => (formik.values.transactionIds as number[]).includes(x.id))
      .reduce((acc, x) => (acc += Number(x.amount)), 0);
  }, [formik.values.transactionIds, stakingTransactions]);

  const checkSumAgainstAvailable = () => {
    const availableKey = "available_staked_eth";
    const availableAmount = stakingData && stakingData[availableKey];

    if (availableAmount && sumOfSelectedTransactions > availableAmount) {
      notification.error({
        message: `Sum of selected transactions (${sumOfSelectedTransactions}) is greater than the currently available amount (${availableAmount}). Please unselect some transactions.`,
      });

      return false;
    } else {
      return true;
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className={style.wrapper}>
      <Row gutter={[0, 4]} justify="space-around" align="top">
        <Col
          span={24}
          className={style.header}
          style={{ textAlign: "center", margin: "40px auto" }}
        >
          <Typography.Title>Time-lock staked ETH</Typography.Title>
        </Col>
      </Row>

      <Row gutter={[0, 4]} justify="space-around" align="top">
        <Col sm={24} md={15}>
          <Form className={style.headerWrapperSection}>
            <Section>
              <div className={style.headerWrapper}>
                <CustomSteps
                  current={step}
                  labelPlacement="vertical"
                  items={[
                    {
                      title: "Transactions",
                    },
                    {
                      title: "Timelock",
                    },
                    {
                      title: "Summary",
                    },
                  ]}
                />
              </div>
              <Divider />
              {step === 0 && (
                <TransactionsStep
                  stakingTransactions={stakingTransactions}
                  formik={formik}
                  isUk={isUk}
                  onClickContinue={() => {
                    if (checkSumAgainstAvailable()) {
                      setStep(1);
                      window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }
                  }}
                  onClickCancel={() => history.push(appLink)}
                />
              )}

              {step === 1 && (
                <TimelockStep
                  formik={formik}
                  isUk={isUk}
                  onClickCancel={() => {
                    setStep(0);
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                  onClickContinue={() => {
                    setStep(2);
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                />
              )}

              {step === 2 && (
                <SummaryStep
                  formik={formik}
                  isUk={isUk}
                  sumOfSelectedTransactions={sumOfSelectedTransactions}
                  onClickConfirm={formik.submitForm}
                  onClickCancel={() => {
                    setStep(1);
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                />
              )}
            </Section>
          </Form>
        </Col>

        <Col sm={24} md={7}>
          <Faq faq={timelockStakingFaq} />
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (store: TReducers, ownProps: any) => ({
  investorId: store.userInfo.investor_id,
  companyInvestorId: store.companyReducer.companyDetails.investor_id,
  investorSlug: store.userInfo.nickname,
  country: store.userInfo.country,
  companySlug: store.companyReducer.companyDetails.slug,
});

const mapDispatchToProps = (dispatch: Function) => ({
  setFundsData: (investorId: number) => dispatch(setFundsData(investorId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimelockStakeForm);
